import { Link } from "react-router-dom";
import { Leaf, Phone, Mail, MapPin, Clock, CreditCard, FileText, ShieldCheck } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#2F4F4F] text-[#F0F4F0]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="space-y-6">
            <Link to="/" className="flex items-center gap-2">
              <Leaf className="h-8 w-8 text-[#D2B48C]" />
              <span className="font-serif text-2xl font-semibold tracking-tight">
                Divine Love Wellness Enterprises
              </span>
            </Link>
            <p className="text-[#F0F4F0]/80 leading-relaxed">
              Compassionate mental health care through innovative Ketamine-Assisted Psychotherapy. 
              Your journey to healing begins here.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-xl font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: "Home", path: "/" },
                { name: "About Us", path: "/about" },
                { name: "Our Services", path: "/services" },
                { name: "Intensive Options", path: "/fees" },
                { name: "Book", path: "/book" },
                { name: "Blog", path: "/blog" },
                { name: "FAQ", path: "/faq" },
                { name: "Contact", path: "/contact" },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-[#F0F4F0]/80 hover:text-[#D2B48C] transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-serif text-xl font-semibold mb-6">Services</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/services"
                  className="text-[#F0F4F0]/80 hover:text-[#D2B48C] transition-colors"
                >
                  Ketamine-Assisted Therapy
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className="text-[#F0F4F0]/80 hover:text-[#D2B48C] transition-colors"
                >
                  Ketamine-Assisted Psychotherapy
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className="text-[#F0F4F0]/80 hover:text-[#D2B48C] transition-colors"
                >
                  Individual Counseling
                </Link>
              </li>
              <li>
                <Link
                  to="/services"
                  className="text-[#F0F4F0]/80 hover:text-[#D2B48C] transition-colors"
                >
                  Mental Health Support
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-serif text-xl font-semibold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#D2B48C] mt-0.5 flex-shrink-0" />
                <span className="text-[#F0F4F0]/80" data-testid="footer-address">
                  3206 NE 2nd Ave #1105<br />
                  Miami, FL 33137
                </span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#D2B48C] mt-0.5 flex-shrink-0" />
                <span className="text-[#F0F4F0]/80">
                  Licensed in:<br />
                  Florida, Maine, District of Columbia (DC)
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-[#D2B48C] flex-shrink-0" />
                <a 
                  href="tel:+13059293349" 
                  className="text-[#F0F4F0]/80 hover:text-[#D2B48C] transition-colors"
                  data-testid="footer-phone"
                >
                  (305) 929-3349
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-[#D2B48C] flex-shrink-0" />
                <a 
                  href="mailto:Divinelovewellness@protonmail.com" 
                  className="text-[#F0F4F0]/80 hover:text-[#D2B48C] transition-colors"
                  data-testid="footer-email"
                >
                  Divinelovewellness@protonmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-[#D2B48C] mt-0.5 flex-shrink-0" />
                <span className="text-[#F0F4F0]/80">
                  Hours of Operation:<br />
                  Monday - Saturday EST<br />
                  By Appointment Only
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust Badges */}
        <div
          className="mt-12 pt-8 border-t border-[#F0F4F0]/15"
          data-testid="footer-trust-badges"
        >
          <div className="flex flex-wrap justify-center items-center gap-3 md:gap-4">
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F0F4F0]/5 border border-[#D2B48C]/30 text-[#F0F4F0]/90 text-xs font-medium">
              <CreditCard className="h-3.5 w-3.5 text-[#D2B48C]" />
              HSA &amp; FSA Cards Accepted
            </span>
            <a
              href="https://www.carecredit.com/apply/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F0F4F0]/5 border border-[#D2B48C]/30 text-[#F0F4F0]/90 text-xs font-medium hover:bg-[#F0F4F0]/10 hover:border-[#D2B48C]/50 transition-colors"
              data-testid="footer-carecredit-badge"
            >
              <CreditCard className="h-3.5 w-3.5 text-[#D2B48C]" />
              Financing Available Through CareCredit
            </a>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F0F4F0]/5 border border-[#D2B48C]/30 text-[#F0F4F0]/90 text-xs font-medium">
              <FileText className="h-3.5 w-3.5 text-[#D2B48C]" />
              Superbills for OON Reimbursement
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F0F4F0]/5 border border-[#D2B48C]/30 text-[#F0F4F0]/90 text-xs font-medium">
              <ShieldCheck className="h-3.5 w-3.5 text-[#D2B48C]" />
              HIPAA-Aligned &amp; Confidential
            </span>
            <div
              className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-[#F0F4F0]/5 border border-[#D2B48C]/30"
              data-testid="footer-cctp-badge"
            >
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/i3y2ziu3_IMG_6346.jpeg"
                alt="Evergreen Certified — Certified Clinical Trauma Professional (CCTP)"
                className="h-9 w-9 rounded-full object-cover ring-1 ring-[#D2B48C]/40"
                loading="lazy"
              />
              <span className="text-[#F0F4F0]/90 text-xs font-medium leading-tight">
                Evergreen Certified<br />
                <span className="text-[#F0F4F0]/70">Clinical Trauma Professional (CCTP) · No. 244805</span>
              </span>
            </div>
            <a
              href="https://www.psychologytoday.com/profile/1844694"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center transition-opacity hover:opacity-80"
              data-testid="footer-psychology-today-badge"
              aria-label="View Laura A. Jeanis verified profile on Psychology Today"
            >
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/semz0occ_IMG_4071.jpeg"
                alt="Verified by Psychology Today"
                className="h-11 w-auto rounded-md shadow-sm"
                loading="lazy"
              />
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-[#F0F4F0]/20">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#F0F4F0]/60 text-sm">
              © {currentYear} Divine Love Wellness Enterprises. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <span className="text-[#F0F4F0]/60 hover:text-[#D2B48C] transition-colors cursor-pointer">
                Privacy Policy
              </span>
              <span className="text-[#F0F4F0]/60 hover:text-[#D2B48C] transition-colors cursor-pointer">
                Terms of Service
              </span>
              <span className="text-[#F0F4F0]/60 hover:text-[#D2B48C] transition-colors cursor-pointer">
                HIPAA Compliance
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
